import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Paper, Tabs, Tab, Typography } from '@mui/material';
// The POS hides this strip in full screen — see posFullscreen.js.
import { usePosFullscreen } from './posFullscreen';
import { P } from '../rbac/permissionCodes';
import { hasStoredPermission } from '../rbac/useRequirePerm';

// Restaurant Orders workspace — WORKFLOW CONSOLIDATION ONLY.
//
// A horizontal tab strip over the existing ordering pages, so staff run the
// whole order lifecycle (order -> KOT -> bill -> payment -> history) without
// leaving one workspace. Every tab navigates to an EXISTING route; pages,
// billing, KOT, printing and permissions are untouched.
//
// Dine In / Takeaway / Delivery share the POS page (/admin/CreateOrder) and
// differ only by the ?type= query, which the POS reads to preset its existing
// order-type selector.
// `rbac` is the page-wise switch (Users → Permission Overrides → Restaurant)
// that opens the tab. It sits BESIDE menuKey, not instead of it: menuKey is
// the old grant every user already holds, rbac is the newer per-page switch,
// and a tab shows only when both allow it. A user with no permission codes
// at all passes the rbac test automatically (fail-open), so nothing changes
// for a company that never set up roles.
const TABS = [
  {
    key: 'DineIn',
    label: 'Dine In',
    path: '/admin/CreateOrder',
    type: 'DineIn',
    menuKey: 'CreateOrder',
    rbac: P.POS_DINEIN_OPEN,
  },
  { key: 'RoomService', label: 'Room Service', path: '/admin/Order', menuKey: 'Order', rbac: P.PAGE_ORDERS },
  {
    // Moved out of the sidebar and in here. Booking a table is part of
    // working the floor, not a separate department - and whoever takes the
    // booking is already on this screen looking at the tables.
    //
    // Keeps its own menuKey, so everyone who could reach Reservations before
    // still can, and nobody gains it by the move.
    key: 'TableReservation',
    label: 'Table Reservation',
    path: '/admin/Reservations',
    menuKey: 'Reservations',
    rbac: P.RES_OPEN,
  },
  {
    // Orders the guests placed themselves. Next to Table Reservation because
    // both are "something arrived for the floor to deal with", and whoever is
    // watching one is watching the other.
    //
    // Reuses the CreateOrder key rather than inventing one: a QR order is an
    // order, and a new key nobody has granted would hide the tab from every
    // existing user until somebody remembered to add it.
    key: 'QrOrders',
    label: 'QR Orders',
    path: '/admin/QrOrders',
    menuKey: 'CreateOrder',
    rbac: P.QR_OPEN,
  },
  {
    key: 'Takeaway',
    label: 'Takeaway',
    path: '/admin/CreateOrder',
    type: 'Takeaway',
    menuKey: 'CreateOrder',
    rbac: P.POS_TAKEAWAY_OPEN,
  },
  {
    // Pickup used to be reachable only from the in-page order-type buttons.
    // Those were removed as duplicates, so it gets its own tab here.
    key: 'Pickup',
    label: 'Pickup',
    path: '/admin/CreateOrder',
    type: 'Pickup',
    menuKey: 'CreateOrder',
    rbac: P.POS_PICKUP_OPEN,
  },
  {
    key: 'Delivery',
    label: 'Delivery',
    path: '/admin/CreateOrder',
    type: 'Delivery',
    menuKey: 'CreateOrder',
    rbac: P.POS_DELIVERY_OPEN,
  },
  {
    // Swiggy / Zomato orders arriving from WeraFoods. Beside Delivery: they
    // are delivery orders that came in through an app.
    //
    // Not for everyone yet: its own OPT-IN switch, off for every user until
    // turned On in Users -> Permission Overrides -> Restaurant -> Swiggy /
    // Zomato. Super admins and users without a role do not get it either.
    key: 'OnlineOrders',
    label: 'Swiggy / Zomato',
    path: '/admin/OnlineOrders',
    menuKey: 'CreateOrder',
    rbac: P.POS_ONLINE_ORDERS_OPEN,
  },
  { key: 'QuickBill', label: 'Quick Bill', path: '/admin/QuickBill', menuKey: 'CreateOrder', rbac: P.QB_OPEN },
  { key: 'Tables', label: 'Tables', path: '/admin/TableOrders', menuKey: 'RestaurantOrder', rbac: P.PAGE_TABLE_ORDERS },
  {
    key: 'Payments',
    label: 'Payments',
    path: '/admin/RestaurantPayments',
    menuKey: 'RestaurantOrder',
    rbac: P.RP_OPEN,
  },
  {
    key: 'OrderHistory',
    label: 'Order History',
    path: '/admin/OrderHistory',
    menuKey: 'RestaurantInvoiceReport',
    rbac: P.OH_OPEN,
  },
];

// Which tab this workspace OPENS on, when the address does not say.
//
// Separate from the order of TABS above, and it has to be: that order is what
// reads well across the top, and it puts Room Service and Table Reservation
// second and third. Neither is where somebody who opened "Orders" wants to
// land - they came to take an order.
//
// So: Dine In first. Then the other ways of taking one, counter work last of
// them. Then the watch-and-check screens, only because SOMETHING has to open
// if a user is allowed nothing else.
//
// To change where a restaurant lands, move a line. Anything not listed is
// still reachable, it just never wins the landing.
const LANDING_PRIORITY = [
  'DineIn',
  // Taking an order, by any other route.
  'Takeaway',
  'Pickup',
  'Delivery',
  'QuickBill',
  'RoomService',
  // Things that arrived for the floor to deal with.
  'QrOrders',
  'TableReservation',
  // Nothing to take an order on. Better than an empty screen.
  'Tables',
  'Payments',
  'OrderHistory',
];

/// The highest-priority tab this user may open. Falls back to the first
/// allowed tab, so a tab added to TABS and forgotten here still works.
const landingTab = (tabs) => {
  for (const key of LANDING_PRIORITY) {
    const found = tabs.find((t) => t.key === key);
    if (found) return found;
  }
  return tabs[0];
};

// The page-wise switch, applied after the old menuKey grant.
const rbacAllowed = (tabs) => tabs.filter((t) => hasStoredPermission(t.rbac));

// Same visibility rule as the sidebar: master admin sees all, otherwise the
// stored permission grants decide; fail-open when no permission list exists.
// Exported so the global search can offer these pages too.
//
// They are tabs of this workspace, not sidebar entries, so the search - which
// walks the sidebar menu - could not see them: typing "Dine In" or "QR Orders"
// found nothing. Exporting the SAME list and the SAME permission filter means
// search can never offer a tab the strip would hide, and a tab added here
// appears in search without anyone remembering to add it twice.
export const RESTAURANT_TABS = TABS;
export const allowedRestaurantTabs = () => allowedTabs();

const allowedTabs = () => {
  let user = null;
  try {
    user = JSON.parse(sessionStorage.getItem('user'));
  } catch (_) {}
  if (user?.isMaster) return TABS;

  const raw = sessionStorage.getItem('permissions');
  if (!raw) return rbacAllowed(TABS);
  try {
    const keys = new Set(JSON.parse(raw).map((p) => p.menuKey));
    return rbacAllowed(TABS.filter((t) => keys.has(t.menuKey)));
  } catch (_) {
    return rbacAllowed(TABS);
  }
};

export default function RestaurantOrdersLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const posFullscreen = usePosFullscreen();
  const tabs = React.useMemo(allowedTabs, []);
  const current = location.pathname.replace(/\/+$/, '');
  const typeParam =
    new URLSearchParams(location.search).get('type') || 'DineIn';

  const activeIdx = tabs.findIndex((t) => {
    if (current !== t.path && !current.startsWith(t.path + '/')) return false;
    // The POS route hosts three tabs — disambiguate by ?type=.
    if (t.path === '/admin/CreateOrder') return t.type === typeParam;
    return true;
  });

  const go = (t) =>
    navigate(t.type ? `${t.path}?type=${t.type}` : t.path);

  // Land on a tab this user is actually allowed to open.
  //
  // Hiding the tab was never enough. The sidebar's Orders link points at
  // /admin/CreateOrder with no ?type=, and the default above is DineIn - so a
  // restaurant that runs only Delivery and Quick Bill still opened onto the
  // Dine In floor plan, tables and all, with no Dine In tab above it to
  // explain where they were.
  //
  // Which tab wins is LANDING_PRIORITY, not the order of the strip - see it.
  //
  // Only redirects when the current address IS a tab that is switched off. A
  // sub-page of one of these routes - an order being edited, a booking being
  // opened - has no tab of its own and is left exactly where it is.
  React.useEffect(() => {
    if (tabs.length === 0 || activeIdx !== -1) return;

    const isASwitchedOffTab = TABS.some((t) => {
      if (current !== t.path && !current.startsWith(t.path + '/')) return false;
      if (t.path === '/admin/CreateOrder') return t.type === typeParam;
      return true;
    });
    if (!isASwitchedOffTab) return;

    const land = landingTab(tabs);
    if (!land) return;
    navigate(land.type ? `${land.path}?type=${land.type}` : land.path, {
      replace: true,
    });
  }, [current, typeParam, activeIdx, tabs, navigate]);

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={0}
        square
        sx={{
          // STAYS in POS full screen: the whole restaurant goes full screen,
          // not just the till, so Dine In / Takeaway / Payments / Order
          // History are still one tap away. Only its heading goes (below).
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 1, sm: 2 },
          pt: posFullscreen ? 0 : 1,
          bgcolor: 'background.paper',
        }}>
        {/* The words are the one part of this strip full screen can spare -
            the tabs below say the same thing and are the part you press. */}
        <Typography
          variant='subtitle2'
          sx={{
            display: posFullscreen ? 'none' : 'block',
            fontWeight: 700,
            color: 'text.secondary',
            mb: 0.5,
          }}>
          Restaurant Orders
        </Typography>
        <Tabs
          value={activeIdx === -1 ? false : activeIdx}
          onChange={(_, idx) => go(tabs[idx])}
          variant='scrollable'
          scrollButtons='auto'
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 600,
            },
          }}>
          {tabs.map((t) => (
            <Tab key={t.label} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      {/* The existing page renders below, completely unchanged. */}
      <Outlet />
    </Box>
  );
}